import { compareAsc, parseISO, startOfDay } from "date-fns"
import { toZonedTime } from "date-fns-tz"
import { Display } from "rot-js"
import { Direction, MapID } from "../defines/enums"
import { MapData } from "../defines/types"
import { Dims } from "../defines/values"
import { LockedDoor } from "../entity/other/lockeddoor"
import { Player } from "../player/player"
import { Room } from "./room"
import { TapestryMap, getDoorPosition } from "./tapestrymap"
import { FLOOR_TILE } from "./tile"

export class StaticMap extends TapestryMap {
  constructor(
    mapData: MapData,
    mapID: MapID,
    display: Display,
    player: Player
  ) {
    const viewportWidth = mapData.viewportWidth
      ? mapData.viewportWidth
      : Dims.DEFAULT_VIEWPORT_WIDTH
    const viewportHeight = mapData.viewportHeight
      ? mapData.viewportHeight
      : Dims.DEFAULT_VIEWPORT_HEIGHT

    super(
      mapID,
      display,
      player,
      mapData.width,
      mapData.height,
      viewportWidth,
      viewportHeight,
      mapData.enableFogOfWar
    )

    // 1. Build rooms
    let rooms = this.buildRooms(mapData)

    // 2. Add maze room(s)
    if (mapData.mazeRooms !== undefined) {
      const mazeRoomIndexes = this.addMazeRooms(mapData.mazeRooms, rooms)
      // exclude maze rooms for rest of build
      rooms = rooms.filter((_, idx) => mazeRoomIndexes.includes(idx) == false)
    }

    // 3. Place portals
    let startPoint = this.placePortals(mapData.portals, rooms)

    // 4. Place the player (+1 to move right of the portal)
    startPoint.x += 1
    this.player.setPosition(startPoint)

    // 5. Place popup triggers
    if (mapData.popups !== undefined) {
      this.placePopups(mapData.popups, rooms)
    }

    // 6. Place pigments
    if (mapData.palette !== undefined) {
      this.placePigments(mapData.palette, rooms)
    }

    // 7. Place items
    if (mapData.items !== undefined) {
      this.placeItems(mapData.items, rooms)
    }

    // 8. Place equipment
    if (mapData.equipment !== undefined) {
      this.placeEquipment(mapData.equipment, rooms)
    }

    // 9. Place enemies
    if (mapData.enemies !== undefined) {
      this.placeEnemies(mapData.enemies, rooms)
    }
  }

  private buildRooms(mapData: MapData): Room[] {
    if (mapData.rooms === undefined) {
      throw new Error("Static map must have at least one room")
    }

    let rooms: Room[] = new Array(mapData.rooms.length)

    if (mapData.rooms !== undefined) {
      mapData.rooms.forEach((room, index) => {
        const x = room.x
        const y = room.y
        const newRoom = new Room(x, y, room.width, room.height)
        this.addRoom(newRoom.x, newRoom.y, newRoom.tiles)
        rooms[index] = newRoom
      })
    }

    if (mapData.corridors !== undefined) {
      mapData.corridors.forEach((corr) => {
        const room: Room = rooms[corr.room]
        const position = getDoorPosition(room, corr.entry)
        const length: number = corr.length
        const dir: Direction = corr.direction

        if (dir == Direction.UP) {
          for (let y = position.y; y > position.y - length; --y) {
            this._tiles[y][position.x] = { ...FLOOR_TILE }
            this._tiles[y][position.x].visible = true
          }
        } else if (dir == Direction.DOWN) {
          for (let y = position.y; y < position.y + length; ++y) {
            this._tiles[y][position.x] = { ...FLOOR_TILE }
            this._tiles[y][position.x].visible = true
          }
        } else if (dir == Direction.LEFT) {
          for (let x = position.x; x > position.x - length; --x) {
            this._tiles[position.y][x] = { ...FLOOR_TILE }
            this._tiles[position.y][x].visible = true
          }
        } else if (dir == Direction.RIGHT) {
          for (let x = position.x; x < position.x + length; ++x) {
            this._tiles[position.y][x] = { ...FLOOR_TILE }
            this._tiles[position.y][x].visible = true
          }
        }
      })
    }

    if (mapData.doors !== undefined) {
      const now = toZonedTime(Date.now(), "America/New_York")
      mapData.doors.forEach((d) => {
        let canBeUnlocked = false
        const unlockTime = toZonedTime(
          startOfDay(parseISO(d.unlockDate)),
          "America/New_York"
        )
        if (compareAsc(now, unlockTime) == 1) {
          canBeUnlocked = true
        }

        const position = getDoorPosition(rooms[d.room], d.location)
        this.addEntity(
          new LockedDoor(d.target, position, d.unlockDate, canBeUnlocked)
        )
      })
    }

    return rooms
  }
}
